<template>
  <div class="categories-page">
    <div class="categories-container">
      <!-- Breadcrumb -->
      <nav class="breadcrumb-nav" aria-label="Breadcrumb">
        <a href="/" class="breadcrumb-link"><i class="fas fa-home"></i> Home</a>
        <span class="breadcrumb-separator"><i class="fas fa-chevron-right"></i></span>
        <span class="breadcrumb-current">Categories</span>
      </nav>

      <!-- Hero Header Section -->
      <header class="page-header">
        <div class="header-badge">
          <i class="fas fa-layer-group"></i>
          <span>Departments</span>
        </div>
        <h1 class="page-title">Shop by Category</h1>
        <p class="page-subtitle">
          Explore our complete selection of premium cosmetics and beauty essentials across all categories.
        </p>

        <!-- Stats Overview Pill -->
        <div class="header-stats">
          <div class="stat-pill">
            <span class="stat-value">{{ filteredCategories.length }}</span>
            <span class="stat-label">Categories</span>
          </div>
          <div class="stat-pill">
            <span class="stat-value">{{ totalProductsCount }}</span>
            <span class="stat-label">Products</span>
          </div>
        </div>
      </header>

      <!-- Search and Filter Bar -->
      <div class="filter-toolbar">
        <div class="search-input-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search categories or subcategories..."
            class="search-input"
            aria-label="Search categories"
          />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''"
            class="clear-search-btn"
            title="Clear search"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="sort-wrapper">
          <label for="sortSelect" class="sort-label"><i class="fas fa-arrow-down-short-wide"></i> Sort:</label>
          <select id="sortSelect" v-model="sortBy" class="sort-select">
            <option value="default">Default Order</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
            <option value="products_desc">Most Products</option>
          </select>
        </div>
      </div>

      <!-- Categories Grid -->
      <div v-if="filteredCategories.length > 0" class="categories-grid">
        <article
          v-for="category in filteredCategories"
          :key="category.id"
          class="category-card"
        >
          <!-- Card Image & Overlay -->
          <a :href="`/${category.slug}`" class="card-image-link" :aria-label="category.name">
            <div class="card-image-wrapper">
              <img
                v-if="category.image || category.banner"
                :src="category.image || category.banner"
                :alt="category.name"
                class="category-img"
                loading="lazy"
                @error="onImageError"
              />
              <div v-else class="category-placeholder">
                <i :class="category.icon || 'fas fa-gem'" class="placeholder-icon"></i>
              </div>

              <!-- Product Count Floating Badge -->
              <span class="product-count-badge">
                <i class="fas fa-cube mr-1"></i>
                {{ category.products_count || 0 }} {{ (category.products_count === 1) ? 'Product' : 'Products' }}
              </span>
            </div>
          </a>

          <!-- Card Content -->
          <div class="card-content">
            <div class="card-header-row">
              <a :href="`/${category.slug}`" class="category-title-link">
                <h2 class="category-name">{{ category.name }}</h2>
              </a>
            </div>

            <p v-if="category.meta_description" class="category-desc">
              {{ category.meta_description }}
            </p>

            <!-- Subcategories Chips List -->
            <div v-if="category.sub_categories && category.sub_categories.length > 0" class="subcategories-section">
              <div class="subcategories-chips">
                <a
                  v-for="sub in category.sub_categories.slice(0, 5)"
                  :key="sub.id"
                  :href="`/${category.slug}/${sub.slug}`"
                  class="sub-chip"
                >
                  {{ sub.name }}
                  <span v-if="sub.products_count" class="sub-chip-count">({{ sub.products_count }})</span>
                </a>
                <a
                  v-if="category.sub_categories.length > 5"
                  :href="`/${category.slug}`"
                  class="sub-chip more-chip"
                >
                  +{{ category.sub_categories.length - 5 }} more
                </a>
              </div>
            </div>

            <!-- Card Action Footer -->
            <div class="card-footer">
              <a :href="`/${category.slug}`" class="explore-btn">
                <span>Explore Category</span>
                <i class="fas fa-arrow-right"></i>
              </a>
            </div>
          </div>
        </article>
      </div>

      <!-- Empty State (No search results or no categories) -->
      <div v-else class="empty-state">
        <div class="empty-icon-wrap">
          <i class="fas fa-folder-open"></i>
        </div>
        <h2 class="empty-title">
          {{ searchQuery ? 'No matching categories found' : 'No Categories Available' }}
        </h2>
        <p class="empty-desc">
          {{ searchQuery ? `We couldn't find any categories matching "${searchQuery}".` : 'Check back later for new product categories!' }}
        </p>
        <button v-if="searchQuery" @click="searchQuery = ''" class="reset-search-btn">
          <i class="fas fa-rotate-left mr-2"></i> Reset Search
        </button>
        <a v-else href="/" class="reset-search-btn">
          <i class="fas fa-home mr-2"></i> Return to Homepage
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  categories: {
    type: Array,
    required: true,
    default: () => []
  }
})

const searchQuery = ref('')
const sortBy = ref('default')

const totalProductsCount = computed(() => {
  return props.categories.reduce((acc, cat) => acc + (cat.products_count || 0), 0)
})

const filteredCategories = computed(() => {
  let list = [...props.categories]

  // Filter by search query
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim()
    list = list.filter(cat => {
      const nameMatch = cat.name && cat.name.toLowerCase().includes(q)
      const descMatch = cat.meta_description && cat.meta_description.toLowerCase().includes(q)
      const subMatch = cat.sub_categories && cat.sub_categories.some(sub => sub.name && sub.name.toLowerCase().includes(q))
      return nameMatch || descMatch || subMatch
    })
  }

  // Sort
  if (sortBy.value === 'name_asc') {
    list.sort((a, b) => a.name.localeCompare(b.name))
  } else if (sortBy.value === 'name_desc') {
    list.sort((a, b) => b.name.localeCompare(a.name))
  } else if (sortBy.value === 'products_desc') {
    list.sort((a, b) => (b.products_count || 0) - (a.products_count || 0))
  }

  return list
})

const onImageError = (event) => {
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  if (parent) {
    parent.classList.add('fallback-active')
  }
}
</script>

<style scoped>
.categories-page {
  background-color: #f8fafc;
  min-height: 80vh;
  padding: 2rem 1rem 4rem;
  font-family: inherit;
}

.categories-container {
  max-width: 1280px;
  margin: 0 auto;
}

/* Breadcrumb */
.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 1.5rem;
}

.breadcrumb-link {
  color: #64748b;
  text-decoration: none;
  transition: color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.breadcrumb-link:hover {
  color: #0f172a;
}

.breadcrumb-separator {
  font-size: 0.65rem;
  color: #94a3b8;
}

.breadcrumb-current {
  color: #0f172a;
  font-weight: 600;
}

/* Page Header */
.page-header {
  background: #ffffff;
  border-radius: 1.25rem;
  padding: 2.25rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  text-align: center;
  position: relative;
  overflow: hidden;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.35rem 0.85rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  margin-bottom: 1rem;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.025em;
  margin: 0 0 0.75rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 1rem;
  color: #475569;
  max-width: 600px;
  margin: 0 auto 1.5rem;
  line-height: 1.5;
}

.header-stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 0.4rem 0.875rem;
  border-radius: 9999px;
}

.stat-value {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.875rem;
}

.stat-label {
  color: #64748b;
  font-size: 0.875rem;
}

/* Toolbar */
.filter-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
  background: #ffffff;
  padding: 0.875rem 1.25rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 260px;
  max-width: 480px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.875rem;
}

.search-input {
  width: 100%;
  padding: 0.625rem 2.25rem 0.625rem 2.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.625rem;
  font-size: 0.9rem;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.2s ease;
  outline: none;
}

.search-input:focus {
  background: #ffffff;
  border-color: #0f172a;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.05);
}

.clear-search-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.875rem;
}

.clear-search-btn:hover {
  color: #0f172a;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-label {
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.sort-select {
  padding: 0.55rem 2rem 0.55rem 0.875rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  background: #ffffff url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") no-repeat right 0.5rem center/1.25rem 1.25rem;
  color: #0f172a;
  outline: none;
  cursor: pointer;
  appearance: none;
}

.sort-select:focus {
  border-color: #0f172a;
}

/* Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

/* Card */
.category-card {
  background: #ffffff;
  border-radius: 1.125rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
}

.category-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(15, 23, 42, 0.1);
  border-color: #cbd5e1;
}

.card-image-link {
  display: block;
  text-decoration: none;
  overflow: hidden;
  background: #f1f5f9;
}

.card-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
}

.category-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.category-card:hover .category-img {
  transform: scale(1.05);
}

.category-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #94a3b8;
}

.placeholder-icon {
  font-size: 2.5rem;
  opacity: 0.7;
}

.product-count-badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* Card Content */
.card-content {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-header-row {
  margin-bottom: 0.5rem;
}

.category-title-link {
  text-decoration: none;
  color: inherit;
}

.category-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.35;
  transition: color 0.15s ease;
}

.category-card:hover .category-name {
  color: #0284c7;
}

.category-desc {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0 0 1rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Subcategories Section */
.subcategories-section {
  margin-top: 0.25rem;
  margin-bottom: 1.25rem;
  flex: 1;
}

.subcategories-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.sub-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #334155;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 0.25rem 0.55rem;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: all 0.15s ease;
}

.sub-chip:hover {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.sub-chip-count {
  font-size: 0.7rem;
  opacity: 0.7;
}

.more-chip {
  background: transparent;
  border-style: dashed;
  color: #64748b;
  border-color: #cbd5e1;
}

.more-chip:hover {
  background: #f1f5f9;
  color: #0f172a;
  border-color: #94a3b8;
}

/* Card Action Footer */
.card-footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 0.875rem;
  margin-top: auto;
}

.explore-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  text-decoration: none;
  transition: all 0.15s ease;
}

.explore-btn i {
  font-size: 0.8rem;
  transition: transform 0.2s ease;
}

.category-card:hover .explore-btn {
  color: #0284c7;
}

.category-card:hover .explore-btn i {
  transform: translateX(4px);
}

/* Empty State */
.empty-state {
  background: #ffffff;
  border-radius: 1.25rem;
  border: 1px dashed #cbd5e1;
  padding: 4rem 2rem;
  text-align: center;
  margin-top: 2rem;
}

.empty-icon-wrap {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  margin: 0 auto 1.25rem;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem;
}

.empty-desc {
  font-size: 0.95rem;
  color: #64748b;
  max-width: 420px;
  margin: 0 auto 1.5rem;
}

.reset-search-btn {
  display: inline-flex;
  align-items: center;
  background: #0f172a;
  color: #ffffff;
  padding: 0.65rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease;
}

.reset-search-btn:hover {
  background: #0284c7;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .categories-page {
    padding: 1rem 0.75rem 3rem;
  }

  .page-header {
    padding: 1.75rem 1.25rem;
    margin-bottom: 1.25rem;
    border-radius: 1rem;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .page-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.25rem;
  }

  .filter-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .search-input-wrapper {
    max-width: 100%;
    min-width: 100%;
  }

  .sort-wrapper {
    justify-content: space-between;
  }

  .sort-select {
    flex: 1;
  }

  .categories-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
